/**
 * Path-based routes the SPA understands (nginx serves index.html for all):
 *   /            home
 *   /stats       stats dashboard
 *   /party/CODE  watch party invite
 *   /@handle     claimed shared list
 *   /s/CODE      login-free shared list
 *   /g/GAMEID    top 100 for one category
 *   /LOGIN       open a Twitch channel directly
 */

export interface GameRoute {
  kind: 'game';
  gameId: string;
}

export interface ShareRoute {
  kind: 'share';
  code: string;
}

export interface HandleRoute {
  kind: 'handle';
  handle: string;
}

export type ListRoute = ShareRoute | HandleRoute;

export interface StreamRoute {
  kind: 'stream';
  login: string;
}

export interface ChannelRoute {
  kind: 'channel';
  channelId: string;
}

/** Route prefixes and static assets must never be mistaken for streamers. */
const RESERVED_PATHS = new Set(['stats', 'party', 's', 'g', 'api', 'static']);

export const KNOWN_CHANNEL_IDS = new Set([
  'top', 'rec', 'otk', 'faze', 'ninja', 'amp',
  'gaming', 'politics', 'irl', 'rp', 'followed', 'custom',
  'npxl',
]);

export const parseChannelPath = (
  pathname: string,
  knownChannels?: Array<{ id: string }> | Set<string>,
): ChannelRoute | null => {
  const match = pathname.match(/^\/([a-z0-9_]{1,25})\/?$/i);
  if (!match) return null;
  const slug = match[1].toLowerCase();
  if (RESERVED_PATHS.has(slug)) return null;

  if (knownChannels) {
    if (knownChannels instanceof Set) {
      return knownChannels.has(slug) ? { kind: 'channel', channelId: slug } : null;
    }
    const found = knownChannels.find(c => c.id.toLowerCase() === slug);
    return found ? { kind: 'channel', channelId: found.id } : null;
  }
  return KNOWN_CHANNEL_IDS.has(slug) ? { kind: 'channel', channelId: slug } : null;
};

export const parseStreamPath = (
  pathname: string,
  knownChannels?: Array<{ id: string }> | Set<string>,
): StreamRoute | null => {
  const match = pathname.match(/^\/([a-z0-9_]{1,25})\/?$/i);
  if (!match) return null;
  const login = match[1].toLowerCase();
  if (RESERVED_PATHS.has(login)) return null;
  if (parseChannelPath(pathname, knownChannels)) return null;
  return { kind: 'stream', login };
};

/** Direct streamer links start that streamer. Home and channel routes (like /xl) tune to a channel instead. */
export const startupStreamLogin = (
  pathname: string,
  knownChannels?: Array<{ id: string }> | Set<string>,
): string | null =>
  parseStreamPath(pathname, knownChannels)?.login ?? null;

/** Parse /g/:id — the id is Twitch's numeric category id */
export const parseGamePath = (pathname: string): GameRoute | null => {
  const match = pathname.match(/^\/g\/(\d{1,20})\/?$/);
  return match ? { kind: 'game', gameId: match[1] } : null;
};

/** Parse the two shared-list entry points, /@handle and /s/CODE */
export const parseListPath = (pathname: string): ListRoute | null => {
  const handle = pathname.match(/^\/@([a-z0-9_]{2,20})\/?$/i);
  if (handle) return { kind: 'handle', handle: handle[1].toLowerCase() };
  const share = pathname.match(/^\/s\/([a-z0-9]{4,10})\/?$/i);
  if (share) return { kind: 'share', code: share[1].toUpperCase() };
  return null;
};

export const gamePath = (gameId: string): string => `/g/${gameId}`;

export const sharePath = (code: string): string => `/s/${code}`;

/** Paths the game filter is allowed to rewrite via replaceState — never
 *  clobber a party invite or a shared-list URL someone is sitting on. */
export const canReflectGameInUrl = (pathname: string): boolean =>
  pathname === '/' || parseGamePath(pathname) !== null;

/**
 * Twitch box art comes in two shapes: a `{width}x{height}` template from the
 * games endpoints and a fixed `52x72` URL from category search. Normalize
 * both to the requested size.
 */
export const boxArtUrl = (raw: string | undefined, width: number, height: number): string => {
  if (!raw) return '';
  if (raw.includes('{width}')) {
    return raw.replace('{width}', String(width)).replace('{height}', String(height));
  }
  return raw.replace(/-\d+x\d+(\.\w+)$/, `-${width}x${height}$1`);
};
